import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import JsBarcode from "jsbarcode";
import jsPDF from "jspdf";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import Barcode from "../components/Barcode";
import { Switch } from "../components/ui/switch";
import { Printer, Download, Plus, Minus } from "lucide-react";
import { toast } from "sonner";

// Format presets (mm)
const FORMATS = {
  thermo_90x29: { label: "Thermo 90,3 × 29 mm (Brother DK-11201)", w: 90.3, h: 29, perRow: 1, perCol: 1, mx: 2, my: 2 },
  thermo_50x30: { label: "Thermo 50 × 30 mm", w: 50, h: 30, perRow: 1, perCol: 1, mx: 2, my: 2 },
  thermo_60x40: { label: "Thermo 60 × 40 mm", w: 60, h: 40, perRow: 1, perCol: 1, mx: 3, my: 3 },
  a4_3x8_avery: { label: "A4 Avery 70 × 36 mm (3×8)", w: 70, h: 36, perRow: 3, perCol: 8, mx: 7.2, my: 15.15, pageW: 210, pageH: 297 },
  a4_2x7: { label: "A4 Etiketten 99 × 38 mm (2×7)", w: 99, h: 38, perRow: 2, perCol: 7, mx: 4.5, my: 13.5, pageW: 210, pageH: 297 },
};

function makeBarcodeDataUrl(value) {
  if (!value) return null;
  const canvas = document.createElement("canvas");
  try {
    const fmt = /^\d{13}$/.test(value) ? "EAN13" : "CODE128";
    JsBarcode(canvas, String(value), {
      format: fmt,
      height: 50,
      width: 2,
      displayValue: true,
      fontSize: 14,
      margin: 0,
    });
    return canvas.toDataURL("image/png");
  } catch {
    try {
      JsBarcode(canvas, String(value), { format: "CODE128", height: 50, width: 2, displayValue: true, fontSize: 14, margin: 0 });
      return canvas.toDataURL("image/png");
    } catch {
      return null;
    }
  }
}

function hexToRgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : [249, 115, 22];
}

// Load an article photo into a white square canvas ("contain" fit) so jsPDF
// can place it without distortion regardless of aspect ratio.
function loadSquareThumb(dataUri, sizePx = 240) {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = sizePx;
      c.height = sizePx;
      const ctx = c.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, sizePx, sizePx);
      const scale = Math.min(sizePx / img.width, sizePx / img.height);
      const w = img.width * scale;
      const h = img.height * scale;
      ctx.drawImage(img, (sizePx - w) / 2, (sizePx - h) / 2, w, h);
      resolve(c.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = () => resolve(null);
    img.src = dataUri;
  });
}

// Load an image URL onto a white canvas and return a PNG data URI for jsPDF
function loadImageAsDataUrl(src) {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = img.width;
      c.height = img.height;
      const ctx = c.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, c.width, c.height);
      ctx.drawImage(img, 0, 0);
      resolve(c.toDataURL("image/png"));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

// Rotate an image 90° clockwise (lossless pixel transpose) for portrait labels
function rotateImage90(dataUri) {
  return new Promise((resolve) => {
    if (!dataUri) return resolve(null);
    const img = new window.Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = img.height;
      c.height = img.width;
      const ctx = c.getContext("2d");
      ctx.translate(c.width, 0);
      ctx.rotate(Math.PI / 2);
      ctx.drawImage(img, 0, 0);
      resolve(c.toDataURL("image/png"));
    };
    img.onerror = () => resolve(null);
    img.src = dataUri;
  });
}

export default function Labels() {
  const [searchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState({}); // id -> count
  const [format, setFormat] = useState("thermo_90x29");
  const [orientation, setOrientation] = useState("landscape"); // landscape | portrait (thermo only)
  const [customText, setCustomText] = useState("");
  const [printPhoto, setPrintPhoto] = useState(true);
  const [printColor, setPrintColor] = useState(false);
  const [printLogo, setPrintLogo] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    api.get("/articles").then((r) => {
      setArticles(r.data || []);
      const pre = searchParams.get("article");
      if (pre) setSelected({ [pre]: 1 });
    }).catch(() => setArticles([]));
    api.get("/groups").then((r) => setGroups(r.data || [])).catch(() => setGroups([]));
  }, [searchParams]);

  const groupMap = useMemo(() => {
    const m = {};
    for (const g of groups) m[g.id] = g;
    return m;
  }, [groups]);

  const fmt = FORMATS[format];

  const queue = useMemo(() => {
    const list = [];
    for (const a of articles) {
      const count = selected[a.id] || 0;
      for (let i = 0; i < count; i++) list.push(a);
    }
    return list;
  }, [articles, selected]);

  const inc = (id, by) => {
    setSelected((s) => ({ ...s, [id]: Math.max(0, (s[id] || 0) + by) }));
  };

  const setCount = (id, v) => {
    setSelected((s) => ({ ...s, [id]: Math.max(0, Number(v) || 0) }));
  };

  const generatePdf = async () => {
    if (queue.length === 0) {
      toast.error("Bitte mindestens ein Etikett wählen.");
      return;
    }
    setGenerating(true);
    try {
      const pdf = await buildPdf();
      pdf.save(`etiketten_${format}_${Date.now()}.pdf`);
      toast.success("PDF wurde generiert.");
    } catch {
      toast.error("PDF konnte nicht erzeugt werden.");
    } finally {
      setGenerating(false);
    }
  };

  // Direct print: build the SAME label PDF and open only that in the print
  // dialog (never window.print() — that would print the whole page incl. UI).
  const printDirect = async () => {
    if (queue.length === 0) {
      toast.error("Bitte mindestens ein Etikett wählen.");
      return;
    }
    setGenerating(true);
    try {
      const pdf = await buildPdf();
      // Safari cannot print PDFs from an iframe — download instead
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      if (isSafari) {
        pdf.save(`etiketten_${format}_${Date.now()}.pdf`);
        toast.success("PDF wurde heruntergeladen — bitte aus der PDF-Vorschau drucken.");
        return;
      }
      pdf.autoPrint();
      const blob = pdf.output("blob");
      const url = URL.createObjectURL(blob);
      // Hidden iframe: loads the PDF and triggers its print dialog
      const old = document.getElementById("label-print-frame");
      if (old) old.remove();
      const iframe = document.createElement("iframe");
      iframe.id = "label-print-frame";
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      iframe.src = url;
      iframe.onload = () => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch {
          // Fallback: open the PDF in a new tab (e.g. Safari/Firefox PDF viewer)
          window.open(url, "_blank");
        }
        // Free blob memory after the print dialog had time to load the PDF
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      };
      document.body.appendChild(iframe);
      toast.success("Druckdialog wird geöffnet — es werden nur die Etiketten gedruckt.");
    } catch {
      toast.error("Drucken fehlgeschlagen — bitte den Button PDF ERZEUGEN nutzen.");
    } finally {
      setGenerating(false);
    }
  };

  const buildPdf = async () => {
    const f = FORMATS[format];
    const portraitMode = orientation === "portrait" && !f.pageW;

      // Preload square photo thumbnails (unique per article)
      const thumbs = {};
      if (printPhoto) {
        const unique = [...new Map(queue.map((a) => [a.id, a])).values()].filter(
          (a) => a.image_base64
        );
        for (const a of unique) {
          thumbs[a.id] = await loadSquareThumb(a.image_base64);
        }
      }

      // Portrait: pre-rotate barcode + photo images 90° clockwise
      const rotBarcodes = {};
      const rotThumbs = {};
      if (portraitMode) {
        const unique = [...new Map(queue.map((a) => [a.id, a])).values()];
        for (const a of unique) {
          rotBarcodes[a.id] = await rotateImage90(makeBarcodeDataUrl(a.ean));
          if (thumbs[a.id]) rotThumbs[a.id] = await rotateImage90(thumbs[a.id]);
        }
      }

      // Company logo (bottom right corner)
      const logoData = printLogo ? await loadImageAsDataUrl("/etikett-logo.png") : null;
      const rotLogo = portraitMode && logoData ? await rotateImage90(logoData) : null;

      let pdf;
      // jsPDF defaults to portrait and silently swaps [50,30] to 30x50 —
      // set orientation explicitly so thermo labels stay landscape.
      const orient = f.w >= f.h ? "landscape" : "portrait";
      if (f.pageW) {
        pdf = new jsPDF({ unit: "mm", format: "a4" });
      } else if (portraitMode) {
        pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: [f.h, f.w] });
      } else {
        pdf = new jsPDF({ orientation: orient, unit: "mm", format: [f.w, f.h] });
      }

      const drawLabel = (a, ox, oy) => {
        const group = a.group_id ? groupMap[a.group_id] : null;
        const stripeH = printColor && group?.color ? 2.5 : 0;

        // Group color stripe across the full width (top)
        if (stripeH > 0) {
          const [r, g, b] = hexToRgb(group.color);
          pdf.setFillColor(r, g, b);
          pdf.rect(ox, oy, f.w, stripeH, "F");
        }

        const labelText = customText || a.location_text || a.name;
        pdf.setFontSize(8);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(0, 0, 0);
        // Up to two lines of label text
        const lines = pdf.splitTextToSize(labelText || "", f.w - 5).slice(0, 2);
        lines.forEach((ln, i) =>
          pdf.text(ln, ox + f.w / 2, oy + stripeH + 3.8 + i * 3.4, { align: "center" })
        );
        const textH = 3.8 + (lines.length - 1) * 3.4;

        // "Sonstiges" — strongly highlighted: black bar with white bold text
        let miscH = 0;
        const miscText = (a.misc || "").trim();
        if (miscText) {
          const barY = oy + stripeH + textH + 1.2;
          const barH = 4.2;
          pdf.setFillColor(0, 0, 0);
          pdf.rect(ox + 1.5, barY, f.w - 3, barH, "F");
          pdf.setTextColor(255, 255, 255);
          const miscLine = pdf.splitTextToSize(miscText.toUpperCase(), f.w - 6)[0];
          pdf.text(miscLine, ox + f.w / 2, barY + 3, { align: "center" });
          pdf.setTextColor(0, 0, 0);
          miscH = barH + 1.2;
        }

        const contentY = oy + stripeH + textH + miscH + 1.7;
        const contentH = f.h - stripeH - textH - miscH - 3.2;
        const thumb = printPhoto ? thumbs[a.id] : null;
        const dataUrl = makeBarcodeDataUrl(a.ean);

        // Company logo bottom right
        const lg = logoData ? Math.min(9, Math.max(6, contentH - 1)) : 0;
        if (lg) {
          pdf.addImage(logoData, "PNG", ox + f.w - lg - 2, oy + f.h - lg - 1.5, lg, lg);
        }
        const rightMargin = lg ? lg + 3.5 : 1.5;

        if (thumb) {
          // Photo on the left, barcode on the right
          const ph = Math.max(8, Math.min(contentH - 0.5, 16));
          pdf.addImage(thumb, "JPEG", ox + 1.5, contentY, ph, ph);
          const bx0 = ox + 1.5 + ph + 1.5;
          const availW = ox + f.w - rightMargin - bx0;
          if (dataUrl && availW > 10) {
            const bw = Math.min(availW, 40);
            const bh = Math.min(contentH - 1, 16);
            pdf.addImage(dataUrl, "PNG", bx0 + (availW - bw) / 2, contentY + Math.max(0, (ph - bh) / 2), bw, bh);
          }
        } else if (dataUrl) {
          const availW = f.w - 3 - (lg ? lg + 2 : 0);
          const bw = Math.min(availW, 42);
          const bh = Math.min(contentH, 20);
          pdf.addImage(dataUrl, "PNG", ox + 1.5 + (availW - bw) / 2, contentY, bw, bh);
        }
      };

      // Portrait page [f.h × f.w]: whole label rotated 90° clockwise.
      // Landscape point (lx, ly) maps to portrait (f.h - ly, lx).
      const drawLabelPortrait = (a) => {
        const group = a.group_id ? groupMap[a.group_id] : null;
        const stripeH = printColor && group?.color ? 2.5 : 0;

        // Color stripe: landscape top edge → portrait right edge
        if (stripeH > 0) {
          const [r, g, b] = hexToRgb(group.color);
          pdf.setFillColor(r, g, b);
          pdf.rect(f.h - stripeH, 0, stripeH, f.w, "F");
        }

        const labelText = customText || a.location_text || a.name;
        pdf.setFontSize(8);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(0, 0, 0);
        // Up to two lines, rotated 90° clockwise, reading top → bottom.
        // jsPDF's align:"center" is broken with angle — center manually.
        const lines = pdf.splitTextToSize(labelText || "", f.w - 5).slice(0, 2);
        lines.forEach((ln, i) => {
          const tw = pdf.getTextWidth(ln);
          pdf.text(ln, f.h - (stripeH + 3.8 + i * 3.4), f.w / 2 - tw / 2, { angle: -90 });
        });
        const textH = 3.8 + (lines.length - 1) * 3.4;

        // "Sonstiges" — black bar, mapped to portrait (rotated 90° cw)
        let miscH = 0;
        const miscText = (a.misc || "").trim();
        if (miscText) {
          const barY = stripeH + textH + 1.2; // landscape y
          const barH = 4.2;
          pdf.setFillColor(0, 0, 0);
          pdf.rect(f.h - barY - barH, 1.5, barH, f.w - 3, "F");
          pdf.setTextColor(255, 255, 255);
          const miscLine = pdf.splitTextToSize(miscText.toUpperCase(), f.w - 6)[0];
          const mtw = pdf.getTextWidth(miscLine);
          pdf.text(miscLine, f.h - (barY + 3), f.w / 2 - mtw / 2, { angle: -90 });
          pdf.setTextColor(0, 0, 0);
          miscH = barH + 1.2;
        }

        const contentY = stripeH + textH + miscH + 1.7;
        const contentH = f.h - stripeH - textH - miscH - 3.2;
        const thumb = printPhoto ? rotThumbs[a.id] : null;
        const barcode = rotBarcodes[a.id];

        // Company logo: landscape bottom-right → portrait (1.5, f.w - lg - 2)
        const lg = rotLogo ? Math.min(9, Math.max(6, contentH - 1)) : 0;
        if (lg) {
          pdf.addImage(rotLogo, "PNG", 1.5, f.w - lg - 2, lg, lg);
        }
        const rightMargin = lg ? lg + 3.5 : 1.5;

        if (thumb) {
          const ph = Math.max(8, Math.min(contentH - 0.5, 16));
          pdf.addImage(thumb, "PNG", f.h - contentY - ph, 1.5, ph, ph);
          const bx0 = 1.5 + ph + 1.5;
          const availW = f.w - rightMargin - bx0;
          if (barcode && availW > 10) {
            const bw = Math.min(availW, 40);
            const bh = Math.min(contentH - 1, 16);
            const by = contentY + Math.max(0, (ph - bh) / 2);
            pdf.addImage(barcode, "PNG", f.h - by - bh, bx0 + (availW - bw) / 2, bh, bw);
          }
        } else if (barcode) {
          const availW = f.w - 3 - (lg ? lg + 2 : 0);
          const bw = Math.min(availW, 42);
          const bh = Math.min(contentH, 20);
          pdf.addImage(barcode, "PNG", f.h - contentY - bh, 1.5 + (availW - bw) / 2, bh, bw);
        }
      };

      if (f.pageW) {
        const cols = f.perRow;
        const rows = f.perCol;
        const perPage = cols * rows;
        queue.forEach((a, idx) => {
          const pageIdx = Math.floor(idx / perPage);
          const inPage = idx % perPage;
          if (inPage === 0 && pageIdx > 0) pdf.addPage();
          const c = inPage % cols;
          const r = Math.floor(inPage / cols);
          const colGap = (f.pageW - 2 * f.mx - cols * f.w) / Math.max(1, cols - 1);
          const rowGap = (f.pageH - 2 * f.my - rows * f.h) / Math.max(1, rows - 1);
          const x = f.mx + c * (f.w + colGap);
          const y = f.my + r * (f.h + rowGap);
          drawLabel(a, x, y);
        });
      } else if (portraitMode) {
        queue.forEach((a, idx) => {
          if (idx > 0) pdf.addPage([f.h, f.w], "portrait");
          drawLabelPortrait(a);
        });
      } else {
        queue.forEach((a, idx) => {
          if (idx > 0) pdf.addPage([f.w, f.h], orient);
          drawLabel(a, 0, 0);
        });
      }
      return pdf;
  };

  const previewItem = queue[0] || articles.find((a) => selected[a.id]) || null;

  return (
    <div className="max-w-[1500px] mx-auto space-y-6" data-testid="labels-page">
      <div>
        <div className="mono text-[11px] tracking-[0.25em] text-zinc-500 mb-2">
          ETIKETTEN · EAN-DRUCK
        </div>
        <h1 className="font-display text-4xl uppercase">Etiketten drucken</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Article selection */}
        <div className="lg:col-span-3 bg-white border border-zinc-200 rounded-sm">
          <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
            <div>
              <div className="mono text-[10px] tracking-[0.25em] text-zinc-500">
                AUSWAHL · {queue.length} ETIKETTEN
              </div>
              <div className="font-display text-lg uppercase">Artikel wählen</div>
            </div>
          </div>
          <div className="divide-y divide-zinc-100 max-h-[60vh] overflow-y-auto">
            {articles.length === 0 && (
              <div className="text-sm text-zinc-500 px-5 py-8 text-center">
                Noch keine Artikel angelegt.
              </div>
            )}
            {articles.map((a) => {
              const count = selected[a.id] || 0;
              return (
                <div
                  key={a.id}
                  data-testid={`label-row-${a.id}`}
                  className={`px-5 py-3 flex items-center gap-4 ${count > 0 ? "bg-orange-50" : ""}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{a.name}</div>
                    <div className="mono text-[11px] text-zinc-500">
                      {a.ean || "— keine EAN —"}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => inc(a.id, -1)}
                      className="w-8 h-8 border border-zinc-400 rounded-sm hover:bg-zinc-50 flex items-center justify-center"
                      data-testid={`label-dec-${a.id}`}
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <Input
                      type="number"
                      value={count}
                      onChange={(e) => setCount(a.id, e.target.value)}
                      data-testid={`label-count-${a.id}`}
                      className="w-14 h-8 rounded-sm border-zinc-400 text-center font-mono-num px-1"
                    />
                    <button
                      onClick={() => inc(a.id, 1)}
                      className="w-8 h-8 border border-zinc-400 rounded-sm hover:bg-zinc-50 flex items-center justify-center"
                      data-testid={`label-inc-${a.id}`}
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Settings + preview */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-zinc-200 rounded-sm p-5">
            <div className="mono text-[10px] tracking-[0.25em] text-zinc-500 mb-3">FORMAT</div>
            <Select value={format} onValueChange={setFormat}>
              <SelectTrigger className="rounded-sm border-zinc-400" data-testid="label-format-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-sm">
                {Object.entries(FORMATS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {!fmt.pageW && (
              <div className="mt-4">
                <Label className="text-xs uppercase tracking-wider font-semibold">
                  Ausrichtung
                </Label>
                <div className="mt-1.5 grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setOrientation("landscape")}
                    data-testid="orientation-landscape-button"
                    className={`h-10 rounded-sm text-sm font-medium border transition-colors ${
                      orientation === "landscape"
                        ? "bg-[#F97316] border-[#F97316] text-white"
                        : "border-zinc-400 hover:bg-zinc-50 text-zinc-700"
                    }`}
                  >
                    Querformat
                  </button>
                  <button
                    onClick={() => setOrientation("portrait")}
                    data-testid="orientation-portrait-button"
                    className={`h-10 rounded-sm text-sm font-medium border transition-colors ${
                      orientation === "portrait"
                        ? "bg-[#F97316] border-[#F97316] text-white"
                        : "border-zinc-400 hover:bg-zinc-50 text-zinc-700"
                    }`}
                  >
                    Hochformat
                  </button>
                </div>
                <div className="mt-1 text-[11px] text-zinc-500">
                  {orientation === "landscape" ? `${fmt.w} × ${fmt.h} mm` : `${fmt.h} × ${fmt.w} mm`}
                </div>
              </div>
            )}

            <div className="mt-4">
              <Label className="text-xs uppercase tracking-wider font-semibold">
                Beschriftung (überschreibt)
              </Label>
              <Input
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                placeholder="leer = Beschriftung des Artikels"
                data-testid="custom-label-text-input"
                className="mt-1.5 rounded-sm border-zinc-400"
              />
            </div>

            <div className="mt-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wider font-semibold">
                  Foto drucken
                </Label>
                <Switch
                  checked={printPhoto}
                  onCheckedChange={setPrintPhoto}
                  data-testid="print-photo-switch"
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wider font-semibold">
                  Gruppen-Farbe drucken
                </Label>
                <Switch
                  checked={printColor}
                  onCheckedChange={setPrintColor}
                  data-testid="print-color-switch"
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wider font-semibold">
                  Logo drucken
                </Label>
                <Switch
                  checked={printLogo}
                  onCheckedChange={setPrintLogo}
                  data-testid="print-logo-switch"
                />
              </div>
            </div>

            <Button
              onClick={generatePdf}
              disabled={generating}
              data-testid="generate-pdf-button"
              className="w-full mt-5 bg-[#F97316] hover:bg-[#EA580C] text-white rounded-sm h-11 font-semibold tracking-wide"
            >
              <Download className="w-4 h-4 mr-2" /> {generating ? "ERZEUGE…" : `PDF ERZEUGEN (${queue.length})`}
            </Button>
            <button
              onClick={printDirect}
              disabled={generating}
              className="w-full mt-2 border border-zinc-400 hover:bg-zinc-50 h-10 rounded-sm text-sm font-medium flex items-center justify-center"
              data-testid="print-button"
            >
              <Printer className="w-4 h-4 mr-2" /> Direkt drucken (nur Etiketten)
            </button>
          </div>

          {previewItem && (() => {
            const pvGroup = previewItem.group_id ? groupMap[previewItem.group_id] : null;
            const pvStripe = printColor && pvGroup?.color;
            const pvPhoto = printPhoto && previewItem.image_base64;
            const pvPortrait = orientation === "portrait" && !fmt.pageW;
            return (
              <div className="bg-white border border-zinc-200 rounded-sm p-5">
                <div className="mono text-[10px] tracking-[0.25em] text-zinc-500 mb-3">VORSCHAU</div>
                <div
                  className="mx-auto bg-white border-2 border-dashed border-zinc-300 overflow-hidden"
                  style={{
                    width: `${(pvPortrait ? fmt.h : fmt.w) * 3}px`,
                    height: `${(pvPortrait ? fmt.w : fmt.h) * 3}px`,
                  }}
                  data-testid="label-preview"
                >
                  <div
                    className="flex flex-col relative"
                    style={
                      pvPortrait
                        ? {
                            width: `${fmt.w * 3}px`,
                            height: `${fmt.h * 3}px`,
                            transform: "rotate(90deg) translateY(-100%)",
                            transformOrigin: "top left",
                          }
                        : { width: "100%", height: "100%" }
                    }
                  >
                    {pvStripe && (
                      <div
                        className="w-full shrink-0"
                        style={{ height: "8px", backgroundColor: pvGroup.color }}
                        data-testid="label-preview-color-stripe"
                      />
                    )}
                    {printLogo && (
                      <img
                        src="/etikett-logo.png"
                        alt=""
                        className="absolute bottom-1 right-1 w-6 h-6 object-contain"
                        data-testid="label-preview-logo"
                      />
                    )}
                    <div className="flex-1 flex flex-col items-center justify-center p-1.5 min-h-0">
                      <div
                        className="text-center font-semibold text-xs sm:text-sm leading-tight px-1"
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {customText || previewItem.location_text || previewItem.name}
                      </div>
                      {(previewItem.misc || "").trim() && (
                        <div
                          className="bg-black text-white font-bold text-[10px] sm:text-xs uppercase tracking-wide text-center px-2 py-0.5 mt-0.5 w-[92%] truncate"
                          data-testid="label-preview-misc"
                        >
                          {previewItem.misc}
                        </div>
                      )}
                      <div className="flex items-center justify-center gap-2 min-h-0 flex-1 w-full">
                        {pvPhoto && (
                          <img
                            src={previewItem.image_base64}
                            alt=""
                            className="object-contain border border-zinc-200 bg-white shrink-0"
                            style={{ width: `${Math.min(fmt.h * 1.6, 48)}px`, height: `${Math.min(fmt.h * 1.6, 48)}px` }}
                            data-testid="label-preview-photo"
                          />
                        )}
                        {previewItem.ean && (
                          <Barcode
                            value={previewItem.ean}
                            height={Math.min(pvPhoto ? 38 : 45, fmt.h * 1.3)}
                            width={pvPhoto ? 1.1 : 1.4}
                            fontSize={10}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-2 text-[11px] text-zinc-500 text-center">
                  Tatsächliche Größe: {pvPortrait ? `${fmt.h} × ${fmt.w}` : `${fmt.w} × ${fmt.h}`} mm
                  {pvStripe ? ` · Gruppe: ${pvGroup.name}` : ""}
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
